import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecordingGuardContext } from '../contexts/RecordingGuardContext';
import { useScribeNav } from '../contexts/ScribeNavContext';
import type { TabId } from '../constants/scribeUi';
import type { UsageData } from './useUsage';

export const SCRIBE_PATH = '/';

export function getUsageBarColor(percent: number): 'success' | 'warning' | 'error' {
  if (percent >= 90) return 'error';
  if (percent >= 70) return 'warning';
  return 'success';
}

export interface NavUsageDisplay {
  percent: number;
  barColor: 'success' | 'warning' | 'error';
  /** Compact form for the rail, e.g. "$12.50 / $15.00". */
  text: string;
  /** Tooltip and accessible detail for the rail's Usage item. */
  detail: string;
  /** The phone avatar menu's Usage item. */
  menuLabel: string;
}

/**
 * The Usage item and the Upgrade button shown by both the rail and the phone avatar menu.
 * `display` is null while usage is loading, unavailable, or the plan has no limit; `exceeded`
 * is true once the monthly limit is used up.
 */
export function getNavUsage(
  usage: UsageData | null,
  usageLoading: boolean,
): { display: NavUsageDisplay | null; exceeded: boolean } {
  const exceeded = usage !== null && usage.limit !== null && usage.used >= usage.limit;
  if (usageLoading || usage === null || usage.limit === null) return { display: null, exceeded };
  const used = `$${usage.used.toFixed(2)}`;
  const limit = `$${usage.limit.toFixed(2)}`;
  return {
    exceeded,
    display: {
      percent: usage.percentUsed,
      barColor: getUsageBarColor(usage.percentUsed),
      text: `${used} / ${limit}`,
      detail: `${usage.totalJobs} jobs this month · ${used} of ${limit} AI budget used`,
      menuLabel: `Usage: ${used} of ${limit}`,
    },
  };
}

/**
 * What the rail (desktop) and the bottom bar and avatar menu (phone) do when used.
 * Changing tool inside Scribe goes through the tab guard; everything that leaves the page
 * asks confirmLeave() first, so a live recording is never dropped silently.
 */
export function useAppNavActions(onSignOut: () => void) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { confirmLeave } = useRecordingGuardContext();
  const { activeTab, requestTabChange, selectTab } = useScribeNav();

  const onScribe = pathname === SCRIBE_PATH;

  const selectTool = useCallback(
    async (id: TabId) => {
      if (onScribe) {
        requestTabChange(id);
        return;
      }
      if (await confirmLeave()) {
        selectTab(id);
        navigate(SCRIBE_PATH);
      }
    },
    [onScribe, requestTabChange, confirmLeave, selectTab, navigate],
  );

  const goTo = useCallback(
    async (to: string) => {
      if (await confirmLeave()) navigate(to);
    },
    [confirmLeave, navigate],
  );

  const signOut = useCallback(async () => {
    if (await confirmLeave()) onSignOut();
  }, [confirmLeave, onSignOut]);

  return {
    /** The tool shown as active, or null on pages that are not Scribe. */
    activeTool: onScribe ? activeTab : null,
    selectTool,
    goTo,
    signOut,
  };
}
